"use client";

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { ChevronDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import AlertMsg from "@/components/dialogs/common/AlertMsg";
import { SHOW_MESSAGE } from "@/lib/constant/constant";
import { zodResolver } from "@hookform/resolvers/zod";
import { editOrganisationSchema } from "@/utils/schema/editOrganisationSchema";
import { useUpdateTenantMutation } from "@/store/slices/apiSlice";
const industryOptions = [
  "Manufacturing",
  "Healthcare",
  "Education",
  "Finance",
  "Technology",
  "Retail",
];

const initAlertMsg = {
  open: false,
  label: "",
  msg: "",
  isError: false,
  isLoading: false,
  btnText: "",
  canClose: false,
};

const EditOrganisationDialog = ({
  open,
  onClose,
  organisationDetails,
  onSave,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [alertMsg, setAlertMsg] = useState(initAlertMsg);
  const [updateTenant] = useUpdateTenantMutation();
  const form = useForm({
    resolver: zodResolver(editOrganisationSchema),
    defaultValues: {
      organisationName: organisationDetails?.orgName || "",
      industryType: organisationDetails?.industryType || "",
      contactPerson: organisationDetails?.contactPerson || "",
      contactEmail: organisationDetails?.email || "",
      contactNumber: organisationDetails?.contactNumber || "",
      addressLine1: organisationDetails?.address || "",
      city: organisationDetails?.city || "",
      state: organisationDetails?.state || "",
      zip: organisationDetails?.zip || "",
      website: organisationDetails?.website || "",
      deeplink: organisationDetails?.deeplinkText || "",
    },
  });
  useEffect(() => {
    if (organisationDetails) {
      form.reset({
        organisationName: organisationDetails?.orgName || "",
        industryType: organisationDetails?.industryType || "",
        contactPerson: organisationDetails?.contactPerson || "",
        contactEmail: organisationDetails?.email || "",
        contactNumber: organisationDetails?.contactNumber || "",
        addressLine1: organisationDetails?.address || "",
        city: organisationDetails?.city || "",
        state: organisationDetails?.state || "",
        zip: organisationDetails?.zip || "",
        website: organisationDetails?.website || "",
        deeplink: organisationDetails?.deeplinkText || "",
      });
    }
  }, [organisationDetails, form]);
  const handleAlertClose = () => {
    const wasError = alertMsg?.isError;

    setAlertMsg(initAlertMsg);

    if (!wasError) {
      form.reset();
      onClose?.();
    }
  };

  const handleDialogClose = () => {
    if (isLoading) return;

    form.reset();
    onClose?.();
  };

  const handleSubmit = async (values) => {
    try {
      setIsLoading(true);
      const payload = {
        orgName: values.organisationName,
        industryType: values.industryType,
        contactPerson: values.contactPerson,
        email: values.contactEmail,
        contactNumber: values.contactNumber,
        address: values.addressLine1,
        city: values.city,
        state: values.state,
        zip: values.zip,
        website: values.website,
        deeplink: values.deeplink,
      };

      // console.log("Edit organisation payload:", payload);

      const res = await updateTenant({
        tenantId: organisationDetails?._id,
        payload,
      }).unwrap();
      if (res?.response?.statusEnum === 1) {
        onSave?.(payload);
        setAlertMsg({
          open: true,
          label: "Organisation Updated Successfully",
          msg:
            res?.response?.message ||
            "Organisation details have been updated successfully.",
          isError: false,
          btnText: "Close",
          canClose: false,
        });
      } else {
        setAlertMsg({
          open: true,
          label: "Update Failed",
          msg:
            res?.response?.message ||
            "Unable to update organisation details. Please try again.",
          isError: true,
          btnText: "Close",
          canClose: false,
        });
      }
    } catch (err) {
      setAlertMsg({
        open: true,
        label: "Update Failed",
        msg:
          err?.data?.response?.message ||
          SHOW_MESSAGE?.SOMETHING_WENT_WRONG ||
          "Something went wrong. Please try again.",
        isError: true,
        btnText: "Close",
        canClose: false,
      });

      console.error("Edit organisation error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogClose}>
        <DialogContent
          className="
            w-[calc(100vw-32px)]
            max-w-[760px]
            max-h-[92vh]
            overflow-y-auto
            rounded-lg
            border
            border-brand-11
            bg-brand-19
            px-5
            py-8
            text-neutral-100
            shadow-2xl
            sm:px-10
            lg:px-14
          "
        >
          <DialogHeader className="mb-6 text-left">
            <DialogTitle className="text-left text-2xl font-bold text-[#67D8FF]">
              Edit Organisation Details
            </DialogTitle>

            <DialogDescription className="sr-only">
              Edit organisation details form
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-5"
            >
              <FormField
                control={form.control}
                name="organisationName"
                rules={{ required: "Organisation name is required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-bold text-neutral-100">
                      Organisation Name
                    </FormLabel>

                    <FormControl>
                      <Input
                        placeholder="Enter organisation name (e.g. Acme Corp)"
                        className="form-dark-input"
                        {...field}
                      />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="industryType"
                rules={{ required: "Industry type is required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-bold text-neutral-100">
                      Industry Type
                    </FormLabel>

                    <FormControl>
                      <CustomSelect
                        value={field.value}
                        placeholder="Select an industry"
                        options={industryOptions}
                        onChange={field.onChange}
                      />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="contactPerson"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-bold text-neutral-100">
                      Contact Person
                    </FormLabel>

                    <FormControl>
                      <Input
                        placeholder="Enter full name (e.g. John Doe)"
                        className="form-dark-input"
                        {...field}
                      />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="contactEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-bold text-neutral-100">
                        Contact Email
                      </FormLabel>

                      <FormControl>
                        <Input
                          placeholder="Enter email address (e.g. john@acme.com)"
                          className="form-dark-input"
                          {...field}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="contactNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-bold text-neutral-100">
                        Contact Number
                      </FormLabel>

                      <FormControl>
                        <Input
                          placeholder="Enter phone number (e.g. +1 234 567 8900)"
                          className="form-dark-input"
                          {...field}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="addressLine1"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-bold text-neutral-100">
                      Organisation Address
                    </FormLabel>

                    <FormControl>
                      <Input
                        placeholder="Address line 1"
                        className="form-dark-input"
                        {...field}
                      />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="City"
                          className="form-dark-input"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="state"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="State"
                          className="form-dark-input"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="zip"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="Zip"
                          className="form-dark-input"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="website"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-bold text-neutral-100">
                      Website (Optional)
                    </FormLabel>

                    <FormControl>
                      <Input
                        placeholder="Enter website URL (e.g. www.acme.com)"
                        className="form-dark-input"
                        {...field}
                      />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="deeplink"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-bold text-neutral-100">
                      Deeplink
                    </FormLabel>

                    <FormControl>
                      <Input
                        placeholder="Enter deeplink URL"
                        className="form-dark-input"
                        {...field}
                      />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleDialogClose}
                  disabled={isLoading || form.formState.isSubmitting}
                  className="
                    h-11
                    w-full
                    border-[#607A9B]
                    bg-[#223957]
                    px-8
                    font-bold
                    text-neutral-100
                    hover:bg-[#2B466B]
                    hover:text-neutral-100
                    sm:w-auto
                  "
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  disabled={isLoading || form.formState.isSubmitting}
                  className="
                    h-11
                    w-full
                    bg-brand-21
                    px-8
                    font-bold
                    text-neutral-100
                    hover:bg-brand-22
                    sm:w-auto
                  "
                >
                  {isLoading || form.formState.isSubmitting
                    ? "Saving..."
                    : "Save Changes"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <AlertMsg
        label={alertMsg.label}
        msg={alertMsg.msg}
        open={alertMsg.open}
        onClose={handleAlertClose}
        confirmAction={handleAlertClose}
        isLoading={false}
        isError={alertMsg.isError}
        canClose={alertMsg.canClose}
        confirmationBtnText={alertMsg.btnText}
      />
    </>
  );
};

const CustomSelect = ({ value, placeholder, options, onChange }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="
          flex
          h-11
          w-full
          items-center
          justify-between
          rounded-md
          border
          border-[#14708A]
          bg-[#151515]
          px-4
          text-left
          text-sm
          text-neutral-100
          outline-none
        "
      >
        <span className={value ? "text-neutral-100" : "text-neutral-100/50"}>
          {value || placeholder}
        </span>

        <ChevronDown className="h-5 w-5 text-[#67D8FF]" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-md border border-[#14708A] bg-brand-100 shadow-lg">
          {options.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => {
                onChange(option);
                setOpen(false);
              }}
              className="block w-full px-4 py-3 text-left text-sm text-neutral-100 hover:bg-brand-19"
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default EditOrganisationDialog;
